import React, { useState, useEffect, useRef } from 'react';
import { logBehavior } from '../../Data/Behavior';
import { useSignIn } from '@/Recoil/Session/Session';
import { navigateTo } from '@/Utils/routing';

interface MateAppFlowProps {
    onSuccess: () => void;
    onBack?: () => void;
    showBackButton?: boolean;
}

const MateAppFlow: React.FC<MateAppFlowProps> = ({
    onSuccess,
    onBack,
    showBackButton = true
}) => {
    const { createAuthRequest, pollAuthStatus } = useSignIn();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [authRequest, setAuthRequest] = useState<any>(null);
    const [polling, setPolling] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [pollingFailed, setPollingFailed] = useState(false);
    const [noMATEOnDevice, setNoMATEOnDevice] = useState(false);
    
    const pollingRef = useRef(false);

    const handleMateSignin = async () => {
        try {
            setError(null);
            setIsLoading(true);
            setPolling(false);
            
            const result = await createAuthRequest();
            
            if (result.error) {
                setError(result.error);
                return;
            }
            
            setAuthRequest(result);
            startPolling(result.pincode);
        } catch (err) {
            setError('Failed to create sign-in request. Please try again.');
            console.error('MATE signin error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const startPolling = async (pincode: string) => {
        // Stop any existing polling first
        pollingRef.current = false;
        
        // Small delay to ensure previous polling stops
        await new Promise(resolve => setTimeout(resolve, 100));
        
        setPolling(true);
        pollingRef.current = true;
        
        const poll = async () => {
            if (!pollingRef.current) return;
            
            try {
                const result = await pollAuthStatus(pincode);
                
                if (result.success) {
                    setPolling(false);
                    pollingRef.current = false;
                    logBehavior('login_successful', { 
                        method: 'mate_app',
                        fromUrl: false
                    });
                    setSuccessMessage('Login successful! Redirecting...');
                    
                    // Call success callback
                    setTimeout(() => {
                        onSuccess();
                    }, 1000);
                    return;
                } else if (result.error && result.error.includes('expired')) {
                    setPolling(false);
                    pollingRef.current = false;
                    setError('Sign-in request expired. Please try again.');
                    return;
                }
                
                // Continue polling if still active
                if (pollingRef.current) {
                    setTimeout(poll, 2000); // Poll every 2 seconds
                }
            } catch (err) {
                setPolling(false);
                pollingRef.current = false;
                setPollingFailed(true);
                setError('Connection error. Please refresh to continue.');
                console.error('Polling error:', err);
            }
        };
        
        poll();
    };

    const handleBack = () => {
        setPolling(false);
        pollingRef.current = false;
        setAuthRequest(null);
        setError(null);
        onBack && onBack();
    };

    const handleRetry = () => {
        setError(null);
        setPollingFailed(false);
        handleMateSignin();
    };

    // Stop polling when component unmounts
    useEffect(() => {
        return () => {
            setPolling(false);
            pollingRef.current = false;
        };
    }, []);

    // Auto-start MATE signin when component mounts
    useEffect(() => {
        if (!authRequest && !error) {
            handleMateSignin();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!authRequest && !error && isLoading) {
        return (
            <>
                <div className="auto-verify-message">
                    <p>Preparing MATE sign-in...</p>
                    <div className="loading-spinner"></div>
                </div>
                
                {showBackButton && onBack && (
                    <button
                        type="button"
                        className="back-button"
                        onClick={handleBack}
                        disabled={isLoading}
                    >
                        Back
                    </button>
                )}
            </>
        );
    }

    return (
        <>
            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}
            
            {error ? (
                <div className="error-state">
                    <button
                        type="button"
                        className="submit-button"
                        onClick={handleRetry}
                        disabled={isLoading}
                    >
                        Try Again
                    </button>
                </div>
            ) : (
                <div className="mate-signin">
                    <div className="qr-section">
                        <h3>{"Sign in with MATE App"}</h3>
                        <p style={{whiteSpace: "pre-wrap"}}>{"Scan this QR with your mobile\n(MATE required)"}</p>
                        
                        {authRequest?.qr_image && (
                            <div className={`qr-code ${pollingFailed ? 'qr-code-disabled' : ''}`}>
                                <img 
                                    src={authRequest.qr_image} 
                                    alt="QR Code for MATE sign in"
                                    style={{ width: '200px', height: '200px' }}
                                />
                                {pollingFailed && (
                                    <div className="qr-overlay">
                                        <p>Connection lost</p>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className="pin-code">
                            <p>{"And double check this PIN code in your app:"}</p>
                            <div className="pin-display">{authRequest?.pincode}</div>
                            <p style={{textDecoration:'underline'}} onClick={() => {
                                const schemeUrl = "koreanmate://auth?pincode=" + authRequest?.pincode;
                                let appOpened = false;
                                
                                // Create a timeout to detect if the app didn't open
                                const timeout = setTimeout(() => {
                                    if (!appOpened) {
                                        // User is still on the page, app likely not installed
                                        console.warn('MATE app did not open, setting noMATEOnDevice to true');
                                        setNoMATEOnDevice(true);
                                    }
                                }, 1500);
                                
                                // Track if page becomes hidden (app opened successfully)
                                const handleVisibilityChange = () => {
                                    if (document.hidden) {
                                        appOpened = true;
                                        clearTimeout(timeout);
                                        document.removeEventListener('visibilitychange', handleVisibilityChange);
                                    }
                                };
                                
                                // Track if page loses focus (another indicator app opened)
                                const handleBlur = () => {
                                    appOpened = true;
                                    clearTimeout(timeout);
                                    window.removeEventListener('blur', handleBlur);
                                };
                                
                                document.addEventListener('visibilitychange', handleVisibilityChange);
                                window.addEventListener('blur', handleBlur);
                                
                                // Try to open the custom scheme
                                try {
                                    window.location.href = schemeUrl;
                                } catch (e) {
                                    console.error('Failed to open MATE app:', e);
                                    clearTimeout(timeout);
                                    document.removeEventListener('visibilitychange', handleVisibilityChange);
                                    window.removeEventListener('blur', handleBlur);
                                    setNoMATEOnDevice(true);
                                }
                                
                                // Clean up after a longer timeout
                                setTimeout(() => {
                                    document.removeEventListener('visibilitychange', handleVisibilityChange);
                                    window.removeEventListener('blur', handleBlur);
                                }, 5000);
                            }}>Click Here if you have MATE app in this device</p>
                        </div>

                        {noMATEOnDevice && <div
                            className="pin-code" style={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center"}}
                            onClick={()=>{
                                navigateTo("https://koreanmate.onelink.me/Tkco/y94asact");
                            }}
                        >
                            <p>{"install MATE app"}</p>
                            <div className="mate-icons">
                                <svg className="ios-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                                </svg>
                                <svg className="android-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1518-.5972.416.416 0 00-.5972.1518l-2.0223 3.5046C15.5889 8.2231 13.8956 7.8098 12.0034 7.8098c-1.8922 0-3.5855.4133-5.2077 1.0648L4.7734 5.4592a.4161.4161 0 00-.5972-.1518.416.416 0 00-.1518.5972l1.9973 3.4592C2.6889 11.1867.3432 14.6589 0 18.761h24c-.3432-4.1021-2.6889-7.5743-6.1185-9.4796"/>
                                </svg>
                            </div>
                        </div>}
                        
                        {polling && (
                            <div className="polling-status">
                                <div className="spinner"></div>
                                <p>Waiting for authorization...</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default MateAppFlow;